import { useState, useEffect, useCallback, useRef } from 'react'
import { apiService, type Workspace } from '../services/apiService'
import { getAuthApiService } from '../services/authApi'
import { isValidWorkspaceId } from '../utils/workspaceId'
import { useOrgStore } from '../store/orgStore'
import { pickWorkspaceForOrg, rememberWorkspaceForOrg } from '../utils/orgWorkspaceSelection'

interface UseWorkspaceReturn {
  workspaces: Workspace[]
  activeWorkspace: Workspace | null
  loading: boolean
  error: string | null
  hasWorkspaces: boolean
  setActiveWorkspace: (workspace: Workspace) => void
  loadWorkspaces: () => Promise<void>
  createWorkspace: (data: {
    name: string
    description?: string
    clientOrganization?: string
    organizationId?: string
    templateId?: string
    settings?: Record<string, any>
  }) => Promise<Workspace>
  archiveWorkspace: (workspaceId: string) => Promise<void>
  permanentlyDeleteWorkspace: (workspaceId: string) => Promise<void>
}

interface UseWorkspaceOptions {
  enabled?: boolean
}

// Publish the directory only after every native page has been verified.
// Exact Program admission still uses the authenticated detail read.
let cachedWorkspaces: Workspace[] | null = null
let cachedWorkspacesComplete = false
let isLoadingWorkspaces = false
let loadPromise: Promise<{ workspaces: Workspace[]; error: string | null }> | null = null
let lastLoadTime = 0
let hasLoadedOnce = false // Track if we've completed at least one load
const CACHE_TTL_MS = 30000 // 30 seconds cache
let lastFailureTime = 0
let lastFailureMessage: string | null = null
const FAILURE_BACKOFF_MS = 10000
const WORKSPACES_CACHE_KEY = 'ignis_workspaces_cache'
const WORKSPACES_FAILURE_KEY = 'ignis_workspaces_failure'

function readSessionJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : null
  } catch {
    return null
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // ignore storage failures
  }
}

function clearSessionKey(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // ignore storage failures
  }
}

/**
 * Clear workspace cache - call this on logout to reset state
 */
export function clearWorkspaceCache(): void {
  console.log('🧹 Clearing workspace cache')
  cachedWorkspaces = null
  cachedWorkspacesComplete = false
  isLoadingWorkspaces = false
  loadPromise = null
  lastLoadTime = 0
  hasLoadedOnce = false
  lastFailureTime = 0
  lastFailureMessage = null
  clearSessionKey(WORKSPACES_CACHE_KEY)
  clearSessionKey(WORKSPACES_FAILURE_KEY)
}

export const useWorkspace = (options: UseWorkspaceOptions = {}): UseWorkspaceReturn => {
  const { enabled = true } = options
  const [workspaces, setWorkspaces] = useState<Workspace[]>(cachedWorkspaces || [])
  // FOUNDATIONAL FIX: hydrate activeWorkspace from localStorage on mount.
  // Without this, every page reload starts at null and falls back to
  // picking workspaces[0] (creation-order first), silently overriding the
  // user's prior selection. The localStorage write in setActiveWorkspace
  // is now actually load-bearing instead of write-only.
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(() => {
    try {
      const raw = localStorage.getItem('ignis_active_workspace')
      if (!raw) return null
      const parsed = JSON.parse(raw) as Workspace
      if (parsed && typeof parsed.id === 'string' && parsed.id.length > 0) {
        return parsed
      }
      return null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(enabled && !hasLoadedOnce)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  const loadWorkspaces = useCallback(async () => {
    if (!enabled) {
      if (mountedRef.current) {
        setLoading(false)
        setError(null)
      }
      return
    }

    let authService: ReturnType<typeof getAuthApiService> | null = null
    try {
      authService = getAuthApiService()
    } catch {
      authService = null
    }

    if (!authService?.isAuthenticated()) {
      if (mountedRef.current) {
        setLoading(false)
        setError(null)
      }
      return
    }

    const now = Date.now()
    const persistedCache = readSessionJson<{ workspaces: Workspace[]; fetchedAt: number; complete: boolean }>(WORKSPACES_CACHE_KEY)
    if (cachedWorkspaces === null && persistedCache?.complete === true && Array.isArray(persistedCache.workspaces)) {
      cachedWorkspaces = persistedCache.workspaces
      cachedWorkspacesComplete = true
      lastLoadTime = persistedCache.fetchedAt
    }
    if (cachedWorkspacesComplete && cachedWorkspaces && (now - lastLoadTime) < CACHE_TTL_MS) {
      console.log('⏭️ Using cached workspaces (cache is fresh, has', cachedWorkspaces.length, 'workspaces)')
      setWorkspaces(cachedWorkspaces)
      setLoading(false)
      return
    }

    const persistedFailure = readSessionJson<{ message: string; failedAt: number }>(WORKSPACES_FAILURE_KEY)
    if (!lastFailureMessage && persistedFailure?.message) {
      lastFailureMessage = persistedFailure.message
      lastFailureTime = persistedFailure.failedAt
    }
    if (!cachedWorkspaces && lastFailureMessage && (now - lastFailureTime) < FAILURE_BACKOFF_MS) {
      console.warn('⏭️ Skipping workspace reload during failure cooldown')
      if (mountedRef.current) {
        setError(lastFailureMessage)
        setLoading(false)
      }
      return
    }

    if (isLoadingWorkspaces && loadPromise) {
      console.log('⏭️ Workspace load already in progress, waiting...')
      try {
        const result = await loadPromise
        if (mountedRef.current) {
          setWorkspaces(result.workspaces)
          if (result.error) {
            setError(result.error)
          }
          setLoading(false)
        }
      } catch {
        if (mountedRef.current) {
          setLoading(false)
        }
      }
      return
    }

    console.log('🔍 Loading workspaces from API...')
    isLoadingWorkspaces = true
    setLoading(true)
    setError(null)

    loadPromise = (async () => {
      try {
        console.log('🔍 Calling apiService.getWorkspaces()...')
        const directory: Workspace[] = []
        const seen = new Set<string>()
        const limit = 10
        let total: number | undefined
        let totalPages = 1
        for (let page = 1; page <= totalPages; page += 1) {
          const result = await apiService.getWorkspaces({ page, limit })
          const pagination = result.pagination
          if (!Number.isSafeInteger(pagination.total) || pagination.total < 0 ||
            pagination.page !== page || pagination.limit !== limit ||
            pagination.totalPages !== Math.ceil(pagination.total / limit) ||
            (total !== undefined && pagination.total !== total) ||
            result.workspaces.length !== Math.min(limit, Math.max(pagination.total - (page - 1) * limit, 0))) {
            throw new Error('The complete Program list could not be verified. Retry the read.')
          }
          total = pagination.total
          totalPages = pagination.totalPages
          for (const workspace of result.workspaces) {
            if (seen.has(workspace.id)) {
              throw new Error('The complete Program list could not be verified. Retry the read.')
            }
            seen.add(workspace.id)
            directory.push(workspace)
          }
        }
        const result = { workspaces: directory }
        console.log(
          '✅ API call successful, got',
          result.workspaces.length,
          'workspaces:',
          result.workspaces.map((w) => w.name),
        )
        cachedWorkspaces = result.workspaces
        cachedWorkspacesComplete = true
        lastLoadTime = Date.now()
        lastFailureTime = 0
        lastFailureMessage = null
        writeSessionJson(WORKSPACES_CACHE_KEY, {
          workspaces: result.workspaces,
          fetchedAt: lastLoadTime,
          complete: true,
        })
        clearSessionKey(WORKSPACES_FAILURE_KEY)
        return { workspaces: result.workspaces, error: null }
      } catch (apiError) {
        console.error('❌ Backend API failed:', apiError)
        lastFailureTime = Date.now()
        lastFailureMessage = apiError instanceof Error ? apiError.message : 'Failed to load workspaces'
        writeSessionJson(WORKSPACES_FAILURE_KEY, {
          message: lastFailureMessage,
          failedAt: lastFailureTime,
        })
        return {
          workspaces: [],
          error: lastFailureMessage,
        }
      }
    })()

    try {
      const result = await loadPromise

      if (mountedRef.current) {
        setWorkspaces(result.workspaces)

        if (result.error) {
          setError(result.error)
        }

        if (result.workspaces.length > 0) {
          setActiveWorkspaceState((currentActiveWorkspace) => {
            // Refresh a present row, but do not treat a paginated cache as
            // an authorization decision for a remembered Program.
            if (currentActiveWorkspace) {
              const stillAccessible = result.workspaces.find(
                w => w.id === currentActiveWorkspace.id,
              )
              if (stillAccessible) {
                // Refresh stored copy (name/desc may have changed server-side)
                localStorage.setItem('ignis_active_workspace', JSON.stringify(stillAccessible))
                return stillAccessible
              }
              return currentActiveWorkspace
            }
            // Default to a Program in the organization chosen in the header.
            const activeOrgId = useOrgStore.getState().activeOrg?.id
            const defaultWorkspace = (activeOrgId && pickWorkspaceForOrg(result.workspaces, activeOrgId))
              || result.workspaces[0]
            localStorage.setItem('ignis_active_workspace', JSON.stringify(defaultWorkspace))
            return defaultWorkspace
          })
        }
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error)
      if (mountedRef.current) {
        setError('Failed to load workspaces')
      }
    } finally {
      isLoadingWorkspaces = false
      loadPromise = null
      hasLoadedOnce = true
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [enabled])

  const setActiveWorkspace = useCallback((workspace: Workspace) => {
    if (!isValidWorkspaceId(workspace.id)) {
      console.error('Attempted to set workspace with invalid ID:', workspace.id)
      return
    }
    console.log('🏢 setActiveWorkspace: Switching to workspace:', workspace.id, workspace.name)
    setActiveWorkspaceState(workspace)
    localStorage.setItem('ignis_active_workspace', JSON.stringify(workspace))
    rememberWorkspaceForOrg(workspace)
    // Keep the header organization in step with the chosen Program, using only
    // organizations the user is already known to belong to.
    const { activeOrg, orgs, setActiveOrg } = useOrgStore.getState()
    const organization = orgs.find(org => org.id === workspace.organizationId)
    if (organization && organization.id !== activeOrg?.id) setActiveOrg(organization)
  }, [])

  useEffect(() => {
    if (!enabled || loading || !activeWorkspace?.id || workspaces.some(row => row.id === activeWorkspace.id)) return
    let cancelled = false
    const workspaceId = activeWorkspace.id
    void apiService.getWorkspace(workspaceId).then(workspace => {
      if (workspace.id !== workspaceId || workspace.status !== 'ACTIVE') throw new Error('Program unavailable')
      if (cancelled) return
      setActiveWorkspaceState(current => {
        if (current?.id !== workspaceId) return current
        localStorage.setItem('ignis_active_workspace', JSON.stringify(workspace))
        return workspace
      })
    }).catch(() => {
      if (cancelled) return
      setActiveWorkspaceState(current => {
        if (current?.id !== workspaceId) return current
        localStorage.removeItem('ignis_active_workspace')
        return null
      })
      setError('The selected Program could not be verified. Select a Program or retry the read.')
    })
    return () => { cancelled = true }
  }, [enabled, loading, activeWorkspace?.id, workspaces])

  const createWorkspace = useCallback(
    async (data: {
      name: string
      description?: string
      clientOrganization?: string
      organizationId?: string
      templateId?: string
      settings?: Record<string, any>
    }): Promise<Workspace> => {
      try {
        const newWorkspace = await apiService.createWorkspace({
          name: data.name,
          description: data.description,
          clientOrganization: data.clientOrganization,
          organizationId: data.organizationId,
          settings: data.settings,
        })

        // The create response is authoritative. Publishing it directly keeps the
        // React state and both caches coherent instead of asking loadWorkspaces(),
        // whose normal TTL shortcut can legitimately return the pre-create list.
        setWorkspaces((currentWorkspaces) => {
          const nextWorkspaces = [
            newWorkspace,
            ...currentWorkspaces.filter((workspace) => workspace.id !== newWorkspace.id),
          ]
          cachedWorkspaces = nextWorkspaces
          lastLoadTime = Date.now()
          writeSessionJson(WORKSPACES_CACHE_KEY, {
            workspaces: nextWorkspaces,
            fetchedAt: lastLoadTime,
            complete: cachedWorkspacesComplete,
          })
          return nextWorkspaces
        })

        if (workspaces.length === 0) {
          setActiveWorkspace(newWorkspace)
        }

        return newWorkspace
      } catch (error) {
        console.error('Failed to create workspace:', error)
        throw new Error('Failed to create workspace')
      }
    },
    [workspaces.length, setActiveWorkspace],
  )

  const archiveWorkspace = useCallback(
    async (workspaceId: string) => {
      try {
        await apiService.updateWorkspace(workspaceId, { status: 'ARCHIVED' })

        setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId))

        if (activeWorkspace?.id === workspaceId) {
          const remainingWorkspaces = workspaces.filter((w) => w.id !== workspaceId)
          if (remainingWorkspaces.length > 0) {
            setActiveWorkspace(remainingWorkspaces[0])
          } else {
            setActiveWorkspaceState(null)
            localStorage.removeItem('ignis_active_workspace')
          }
        }
      } catch (error) {
        console.error('Failed to archive workspace:', error)
        throw new Error('Failed to archive workspace')
      }
    },
    [activeWorkspace, workspaces, setActiveWorkspace],
  )

  const permanentlyDeleteWorkspace = useCallback(async (workspaceId: string) => {
    await apiService.permanentlyDeleteWorkspace(workspaceId)
    // Let a GET already in flight settle before applying the authoritative delete,
    // so its older response cannot restore the card we just removed.
    if (loadPromise) await loadPromise
    const remaining = (cachedWorkspaces ?? workspaces).filter(workspace => workspace.id !== workspaceId)
    cachedWorkspaces = remaining
    lastLoadTime = Date.now()
    writeSessionJson(WORKSPACES_CACHE_KEY, { workspaces: remaining, fetchedAt: lastLoadTime, complete: cachedWorkspacesComplete })
    if (!mountedRef.current) return
    setWorkspaces(remaining)
    setActiveWorkspaceState(current => {
      if (current?.id !== workspaceId) return current
      const next = remaining.find(workspace => workspace.status === 'ACTIVE') ?? remaining[0] ?? null
      if (next) localStorage.setItem('ignis_active_workspace', JSON.stringify(next))
      else localStorage.removeItem('ignis_active_workspace')
      return next
    })
  }, [workspaces])

  useEffect(() => {
    mountedRef.current = true

    if (!enabled) {
      setLoading(false)
      setError(null)
      return () => {
        mountedRef.current = false
      }
    }

    const initializeWorkspaces = async () => {
      const savedWorkspace = localStorage.getItem('ignis_active_workspace')
      if (savedWorkspace) {
        try {
          const workspace = JSON.parse(savedWorkspace)
          if (isValidWorkspaceId(workspace?.id)) {
            setActiveWorkspaceState(workspace)
          } else {
            console.warn('Saved workspace has invalid ID format, clearing:', workspace?.id)
            localStorage.removeItem('ignis_active_workspace')
          }
        } catch (error) {
          console.error('Failed to parse saved workspace:', error)
          localStorage.removeItem('ignis_active_workspace')
        }
      }

      await loadWorkspaces()
    }

    initializeWorkspaces()

    return () => {
      mountedRef.current = false
    }
  }, [enabled, loadWorkspaces])

  return {
    workspaces,
    activeWorkspace,
    loading,
    error,
    hasWorkspaces: workspaces.length > 0,
    setActiveWorkspace,
    loadWorkspaces,
    createWorkspace,
    archiveWorkspace,
    permanentlyDeleteWorkspace,
  }
}

export default useWorkspace
